import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { S as SurfaceDarkInfo } from '../Surface/index-945bfaeb.js';
import { T as ThemeProvider } from '../theme/ThemeProvider-f69f6c62.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-701df069.js';
import { M as Message } from './Message-d491ffc4.js';
import { M as MessageHeader } from './MessageHeader-0bb87d65.js';
import { M as MessageIcon } from './MessageIcon-a64bea85.js';
import { C as CloseButton, a as CloseButtonIcon } from './CloseButton-5e12e162.js';
import { T as Text } from '../Text/Text-34987dd4.js';
import { t as times, i as infoCircle, c as check, e as exclamationTriangle, a as exclamation } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-1702ffd3-fa72660f.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-ea7cc233-62dc80e0.js';
import { I as Icon } from '../Icon/Icon-0938d6c4.js';

const StyledContent = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30039493__sc-j3wigw-0"
})(["flex-grow:1;flex-shrink:1;flex-basis:auto;"]);
StyledContent.displayName = 'StyledContent';
const StyledMessageContent = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30039493__sc-j3wigw-1"
})(["", ";ul,ol{color:", ";}", " ", ""], ({
  theme
}) => theme.typography.base, ({
  theme
}) => {
  var _theme$typography;
  return (_theme$typography = theme.typography) === null || _theme$typography === void 0 || (_theme$typography = _theme$typography.color) === null || _theme$typography === void 0 ? void 0 : _theme$typography.base;
}, ({
  $isSolid,
  theme,
  $type
}) => $isSolid && css(["color:", ";a,a:hover,a:focus{color:", ";text-decoration:underline;}"], theme.message[$type].solidColor, theme.message[$type].solidColor), isMobile(css(["", ";"], ({
  theme
}) => theme.typography.mobile.base)));
StyledMessageContent.displayName = 'StyledMessageContent';

// All child elements inside an Alert should have their focus rings overwritten based on the
// Alert style.  To accomplish that, we are overwriting the theme focusVisible properties for
// this component and all children by passing a new theme object to the ThemeProvider.
const focusThemeOverrider = (isSolid, type) => theme => {
  const alertTypeTheme = theme.message[type];

  // Do not modify the theme if there is no associated `type` in the theme data
  if (!alertTypeTheme) {
    return theme;
  }
  const focusBorderColor = isSolid ? alertTypeTheme === null || alertTypeTheme === void 0 ? void 0 : alertTypeTheme.solidFocusBorderColor : alertTypeTheme === null || alertTypeTheme === void 0 ? void 0 : alertTypeTheme.focusBorderColor;
  const focusShadowColor = isSolid ? alertTypeTheme === null || alertTypeTheme === void 0 ? void 0 : alertTypeTheme.solidFocusBorderColor : alertTypeTheme === null || alertTypeTheme === void 0 ? void 0 : alertTypeTheme.focusBorderColor;
  const computedTheme = {
    ...theme,
    focusVisible: {
      ...theme.focusVisible,
      ...(focusBorderColor ? {
        outlineColor: focusBorderColor,
        borderColor: focusBorderColor
      } : {}),
      ...(focusShadowColor ? {
        boxShadowColor: focusShadowColor
      } : {})
    }
  };
  return computedTheme;
};
const chooseIcon = $type => {
  switch ($type) {
    case 'error':
      return /*#__PURE__*/React__default.createElement(Icon, {
        type: exclamation
      });
    case 'warning':
      return /*#__PURE__*/React__default.createElement(Icon, {
        type: exclamationTriangle
      });
    case 'success':
      return /*#__PURE__*/React__default.createElement(Icon, {
        type: check
      });
    default:
      return /*#__PURE__*/React__default.createElement(Icon, {
        type: infoCircle
      });
  }
};
const Alert = ({
  type = 'info',
  header,
  headerLevel = 'h4',
  isSolid = false,
  isOpen = true,
  withCloseButton = false,
  closeButtonLabel = 'Dismiss message',
  children,
  onClose,
  hideIcon = false,
  ...rest
}) => {
  if (!isOpen) return null;
  return /*#__PURE__*/React__default.createElement(ThemeProvider, {
    theme:
    /*
     * This is not optimal. A new theme modifier function is returned on each render.
     * This prevents the ThemeProvider from caching the new theme values.
     */
    focusThemeOverrider(isSolid, type)
  }, /*#__PURE__*/React__default.createElement(Message, _extends({}, rest, {
    as: /* When more surfaces are created, replace this with a surface select function */
    type === 'feature-introduction' ? SurfaceDarkInfo : undefined,
    $type: type,
    $isSolid: isSolid
  }), !hideIcon && !isSolid && /*#__PURE__*/React__default.createElement(MessageIcon, {
    $header: header,
    $type: type
  }, chooseIcon(type)), /*#__PURE__*/React__default.createElement(StyledContent, null, header && !isSolid && /*#__PURE__*/React__default.createElement(MessageHeader, {
    $type: type
  }, /*#__PURE__*/React__default.createElement(Text, {
    type: headerLevel,
    styleAs: "h4"
  }, header)), children && /*#__PURE__*/React__default.createElement(StyledMessageContent, {
    $isSolid: isSolid,
    $type: type
  }, children)), withCloseButton && /*#__PURE__*/React__default.createElement(CloseButton, {
    onClick: onClose
  }, /*#__PURE__*/React__default.createElement(CloseButtonIcon, {
    $isSolid: isSolid,
    type: times,
    $alertType: type,
    header: header
  }), /*#__PURE__*/React__default.createElement(Text, {
    type: "sr-only"
  }, closeButtonLabel))));
};
Alert.propTypes = {
  type: PT.oneOf(['info', 'warning', 'error', 'success', 'feature-introduction']),
  /**
   * The heading text that is rendered by the Alert. This renders as an h4 by default.
   */
  header: PT.node,
  /**
   * The header will always render in the style of an 'h4' but this does not always fit
   * semantically with the rest of your page. Use this prop to change the level of heading
   * you want to render.
   */
  headerLevel: PT.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /**
   * Creates a solid background callout. Does not render header, even if it exists.
   */
  isSolid: PT.bool,
  /**
   * Whether the message is visible. Useful when implementing onClose handler.
   */
  isOpen: PT.bool,
  /**
   * Adds a close icon to dismiss the message. It is up to you to implement onClose handler.
   */
  withCloseButton: PT.bool,
  /**
   * Screen reader-only label for the close button. Provide a translated
   * string via this prop for internationalized UIs.
   */
  closeButtonLabel: PT.string,
  /**
   * Function to perform on message close. Never invoked unless withCloseButton is true.
   */
  onClose: PT.func,
  /**
   * The React nodes that are rendered by the Alert.
   */
  children: PT.oneOfType([PT.arrayOf(PT.node), PT.node]).isRequired,
  /**
   * Hides the Alert specific icon.
   */
  hideIcon: PT.bool,
  /**
   * Adds a small amount of margin-right to the Alert.
   */
  inline: PT.bool
};
Alert.defaultProps = {
  type: 'info',
  isSolid: false,
  isOpen: true,
  withCloseButton: false,
  closeButtonLabel: 'Dismiss message',
  onClose: noop,
  header: undefined,
  headerLevel: 'h4',
  hideIcon: false,
  inline: false
};
var Alert$1 = Alert;

export { Alert$1 as A };
//# sourceMappingURL=Alert-f484c27d.js.map
